import { BACKEND_URL } from '../config';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, ArrowLeft, Check, Languages, LogOut } from 'lucide-react';

const Payment = ({ user, token, language, onLogout, onToggleLanguage, onUpgradeSuccess }) => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handlePayment = async () => {
    setProcessing(true);
    setError('');

    try {
      // Get Razorpay payment link with user info
      const orderResponse = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!orderResponse.ok) {
        throw new Error('Failed to create payment link');
      }

      const data = await orderResponse.json();

      // Redirect to Razorpay payment page
      window.location.href = data.payment_url;

    } catch (err) {
      setError('Payment processing failed. Please try again.');
      setProcessing(false);
    }
  };

  const translations = {
    en: {
      title: 'Upgrade to Premium',
      back: 'Back to Dashboard',
      currentPlan: 'Current Plan',
      choosePlan: 'Choose Your Plan',
      free: 'Free Plan',
      premium: 'Premium Plan',
      perMonth: '/month',
      upgrade: 'Upgrade Now',
      processing: 'Processing Payment...',
      note: 'Note: Payment integration is mocked. Razorpay link will be updated later.'
    },
    hi: {
      title: 'प्रीमियम में अपग्रेड करें',
      back: 'डैशबोर्ड पर वापस जाएं',
      currentPlan: 'वर्तमान योजना',
      choosePlan: 'अपनी योजना चुनें',
      free: 'मुफ्त योजना',
      premium: 'प्रीमियम योजना',
      perMonth: '/महीना',
      upgrade: 'अभी अपग्रेड करें',
      processing: 'भुगतान प्रक्रिया हो रही है...',
      note: 'नोट: भुगतान एकीकरण मॉक है। Razorpay लिंक बाद में अपडेट किया जाएगा।'
    }
  };

  const t = translations[language];

  return (
    <div className="payment-page">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <Leaf size={32} />
            <span>BoAI</span>
          </div>
          <div className="header-actions">
            <button className="lang-toggle" onClick={onToggleLanguage}>
              <Languages size={16} /> {language.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={onLogout}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="payment-content">
        <div className="container">
          <div className="payment-header">
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={18} /> {t.back}
            </button>
            <h2>{t.title}</h2>
          </div>

          <div className="current-plan-info">
            <p>{t.currentPlan}: <strong>{user?.tier?.toUpperCase() || 'FREE'}</strong></p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-badge free">Current</div>
              <h3>{t.free}</h3>
              <div className="price">₹0<span>{t.perMonth}</span></div>
              <ul className="pricing-features">
                <li><Check size={16} /> 5 scans per day</li>
                <li><Check size={16} /> Basic detection</li>
                <li><Check size={16} /> Treatment tips</li>
                <li><Check size={16} /> Hindi & English</li>
              </ul>
            </div>

            <div className="pricing-card featured">
              <div className="pricing-badge paid">Recommended</div>
              <h3>{t.premium}</h3>
              <div className="price">₹499<span>{t.perMonth}</span></div>
              <ul className="pricing-features">
                <li><Check size={16} /> Unlimited scans</li>
                <li><Check size={16} /> Advanced analysis</li>
                <li><Check size={16} /> Detailed treatment</li>
                <li><Check size={16} /> Prevention tips</li>
                <li><Check size={16} /> Full history</li>
                <li><Check size={16} /> Priority support</li>
              </ul>
              <button
                className="btn-primary"
                onClick={handlePayment}
                disabled={processing || user?.tier === 'paid'}
              >
                {processing ? t.processing : (user?.tier === 'paid' ? 'Active' : t.upgrade)}
              </button>
            </div>
          </div>

          <div className="payment-note">
            <p>✓ Secure payment via Razorpay</p>
            <p className="hindi-text">✓ Razorpay के माध्यम से सुरक्षित भुगतान</p>
            <p style={{marginTop: '8px', fontSize: '13px'}}>After payment, you'll be redirected back and upgraded to Premium automatically.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
